import {
    CustomClaimValue, getGroundTruthJobStatusDisplayName, GroundTruthJobStatus,
} from "@app/shared";
import { formatInTimeZone } from "date-fns-tz";
import { AuthorizeView } from "../../../shared/AuthorizeView";
import { LINK_BUTTON, SortableTableHeaderType } from "../../../shared";

/**
 * The columns every ground truth table shares, and the pieces each one adds.
 *
 * Two tables show runs: the facility parking view, which lists one facility's uploads and can
 * promote one to a baseline, and the site-admin tab, which lists the whole fleet plus the anonymous
 * trial runs that belong to no facility at all. They answer different questions and cannot be the
 * same component, but the overlap — when, how many photos, what status, what it counted — is most
 * of both, and had been written twice.
 *
 * Shaped as column *builders* over a minimal row rather than one component with flags: the two
 * tables take different row types (GroundTruthJob and GroundTruthRunSummary), and a shared
 * component would have to be generic over both and then branch internally on which it got. A column
 * is already a function of a row, so sharing columns shares the rendering without pretending the
 * two lists are the same list.
 */

/** What every ground truth row has, whichever table it came from. */
export type GroundTruthRowLike = {
    status: GroundTruthJobStatus;
    photoCount: number;
    totalCount?: number;
    errorMessage?: string;
    requestDate?: Date;
};

/**
 * AsOfDate is non-nullable on the job entity, so rows created before that column existed were
 * backfilled to DateTimeOffset.MinValue (0001-01-01) — a real, parseable Date, so a plain isNaN
 * check does not catch it. No genuine upload is this old.
 */
export function isPlausibleAsOfDate(date?: Date): boolean {
    return date != null && !Number.isNaN(date.getTime()) && date.getUTCFullYear() > 1970;
}

/** A date in the facility's zone, or an em dash. Never the browser's zone — see the zone label. */
export function formatInZone(date: Date | undefined, timeZone: string): string {
    return date && !Number.isNaN(date.getTime())
        ? formatInTimeZone(date, timeZone, "MM/dd/yyyy h:mm aa")
        : "—";
}

export function asOfColumn<T extends GroundTruthRowLike & { asOfDate?: Date }>(
    timeZone: string, timeZoneLabel: string
): SortableTableHeaderType<T> {
    return {
        dataKey: "asOfDate", header: `As Of (${timeZoneLabel})`, width: 150,
        renderFunc: (row) => isPlausibleAsOfDate(row?.asOfDate)
            ? formatInZone(row!.asOfDate, timeZone)
            : "—",
    };
}

/**
 * As Of is the moment the photos describe and can be far in the past — a batch shot last week,
 * uploaded today — so on its own it cannot say when a run actually happened. This is when the batch
 * reached us. Completion is deliberately not a second column: Status already says whether a run
 * finished, and the detail view carries the end-to-end time for when duration matters.
 */
export function uploadedColumn<T extends GroundTruthRowLike>(
    timeZone: string, timeZoneLabel: string
): SortableTableHeaderType<T> {
    return {
        dataKey: "requestDate", header: `Uploaded (${timeZoneLabel})`, width: 150,
        renderFunc: (row) => formatInZone(row?.requestDate, timeZone),
    };
}

export function photosColumn<T extends GroundTruthRowLike>(): SortableTableHeaderType<T> {
    return { dataKey: "photoCount", header: "Photos", width: 60 };
}

export function statusColumn<T extends GroundTruthRowLike>(): SortableTableHeaderType<T> {
    return {
        dataKey: "status", header: "Status", width: 90,
        renderFunc: (row) => getGroundTruthJobStatusDisplayName(row!.status),
    };
}

export function countColumn<T extends GroundTruthRowLike>(): SortableTableHeaderType<T> {
    return {
        dataKey: "totalCount", header: "Count", width: 60,
        renderFunc: (row) => row?.totalCount ?? "—",
    };
}

/**
 * The detail link, superadmin-gated.
 *
 * Offered for failed runs as well as complete ones, which is the opposite of how it started. The
 * gate used to be status === Complete, from when detail meant a composite and a detection list —
 * neither of which a failed run has. Detail now also carries the source photographs, stored before
 * the stitch is attempted, and a failed stitch is precisely the case where those are the only thing
 * worth seeing. Refusing the link there hid the inputs of the one run whose inputs were in question.
 *
 * Still hidden while a run is in flight: there is genuinely nothing recorded yet.
 *
 * Takes the id getter because the two row types name it the same but the trial rows in the admin
 * table are not jobs, and the two are read through different routes.
 */
export function detailColumn<T extends GroundTruthRowLike>(
    getJobId: (row: T) => string | undefined,
    onShowDetail: (jobId: string) => void
): SortableTableHeaderType<T> {
    return {
        header: "", width: 90, sortable: false,
        renderFunc: (row) => {
            const settled = row
                && (row.status === GroundTruthJobStatus.Complete
                    || row.status === GroundTruthJobStatus.Failed);
            const id = settled ? getJobId(row) : undefined;
            if (!id) return null;
            return (
                <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
                    <button
                        type="button"
                        className={`text-link-page hover:text-link-page-hover underline whitespace-nowrap ${LINK_BUTTON}`}
                        onClick={() => onShowDetail(id)}
                    >
                        View detail
                    </button>
                </AuthorizeView>
            );
        },
    };
}
